import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import { QRCodeCanvas } from "qrcode.react";
import {
  Box,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

function ShipmentsList() {
  const [shipments, setShipments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [editData, setEditData] = useState({ idNumber: "", status: "" });
  const [sortField, setSortField] = useState("intakeDate");
  const [sortOrder, setSortOrder] = useState("desc");


  // Fetch shipments from Firestore on component mount
  useEffect(() => {
    fetchShipments();
  }, []);

  const fetchShipments = async () => {
    try {
      const shipmentsCollection = collection(db, "shipments");
      const snapshot = await getDocs(shipmentsCollection);
      const data = snapshot.docs.map((doc) => {
        const shipment = { id: doc.id, ...doc.data() };
  
        // ✅ Extract and normalize Intake Date
        let intakeDate = shipment["Intake Date (MM/DD/YYYY)"];
  
        if (intakeDate && intakeDate.toDate) {
          intakeDate = intakeDate.toDate().toISOString().split("T")[0]; // Convert Firestore Timestamp to YYYY-MM-DD
        } else if (typeof intakeDate === "string" && intakeDate.includes("/")) {
          // Convert MM/DD/YYYY to YYYY-MM-DD
          const [month, day, year] = intakeDate.split("/");
          intakeDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        }
  
        // ✅ Extract ID Number from different field names
        let idNumber =
          shipment["ID Number"] || // New format
          shipment["qrCodeValue"] || // Alternative if used
          shipment["ID Number (intake date MMDDYY-Source-Specific Item: ex: 09142024-Alchemi-Limes)"]; // Old format
  
        return { ...shipment, intakeDate, idNumber };
      });
  
      // ✅ Sort by date in descending order (newest first)
      data.sort((a, b) => new Date(b.intakeDate) - new Date(a.intakeDate));
  
      setShipments(data);
      setFilteredShipments(data);
    } catch (error) {
      console.error("Error fetching shipments:", error);
    }
  };
  
  
  
  const handleSort = (field) => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
  
    const sortedData = [...filteredShipments].sort((a, b) => {
      if (!a[field] || !b[field]) return 0;
  
      if (field === "intakeDate") {
        return newOrder === "asc"
          ? new Date(a[field]) - new Date(b[field])
          : new Date(b[field]) - new Date(a[field]);
      }
  
      return newOrder === "asc"
        ? String(a[field]).localeCompare(String(b[field]))
        : String(b[field]).localeCompare(String(a[field]));
    });
  
    setSortField(field);
    setSortOrder(newOrder);
    setFilteredShipments(sortedData);
  };
  
  

  // Handle search input
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = shipments.filter(
      (shipment) =>
        (shipment["ID Number"] || "").toLowerCase().includes(query) ||
        (shipment["Collection Point"] || "").toLowerCase().includes(query) ||
        (shipment["Specific Food Item"] || "").toLowerCase().includes(query)
    );
    setFilteredShipments(filtered);
  };

  // Handle Edit Click
  const handleEditClick = (shipment) => {
    setSelectedShipment(shipment);
    setEditData({
      idNumber: shipment["ID Number"] || "",
      status: shipment["Next Step"] || "",
    });
    setEditDialogOpen(true);
  };

  // Handle Edit Input Change
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Submit Edit
  const handleEditSubmit = async () => {
    if (!selectedShipment) return;
    try {
      const shipmentRef = doc(db, "shipments", selectedShipment.id);
      await updateDoc(shipmentRef, {
        "ID Number": editData.idNumber,
        "Next Step": editData.status,
      });
      setEditDialogOpen(false);
      fetchShipments();
    } catch (error) {
      console.error("Error updating shipment:", error);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h7" component="h1">
              All Shipments
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                component={Link}
                to="/shipments/upload"
                variant="outlined"
                startIcon={<UploadIcon />}
                sx={{ textTransform: 'none' }}
              >
                Upload Shipments
              </Button>
              <Button
                component={Link}
                to="/shipments/new"
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ textTransform: 'none' }}
              >
                New Shipment
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search shipments (ID, Collection Point, Food Item)"
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell onClick={() => handleSort("idNumber")} style={{ cursor: "pointer" }}>
                    ID Number {sortField === "idNumber" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell onClick={() => handleSort("intakeDate")} style={{ cursor: "pointer" }}>
                    Intake Date {sortField === "intakeDate" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell onClick={() => handleSort("Collection Point")} style={{ cursor: "pointer" }}>
                    Collection Point {sortField === "Collection Point" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell onClick={() => handleSort("Specific Food Item")} style={{ cursor: "pointer" }}>
                    Food Item {sortField === "Specific Food Item" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>QR Code</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredShipments.length > 0 ? (
                  filteredShipments.map((shipment, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{shipment.idNumber || "N/A"}</TableCell>
                      <TableCell>{shipment.intakeDate || "N/A"}</TableCell>
                      <TableCell>{shipment["Collection Point"] || "N/A"}</TableCell>
                      <TableCell>{shipment["Specific Food Item"] || "N/A"}</TableCell>
                      <TableCell>{shipment["Next Step"] || "N/A"}</TableCell>
                      <TableCell>
                        {shipment.qrCodeValue ? (
                          <QRCodeCanvas value={shipment.qrCodeValue} size={50} />
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            component={Link}
                            to={`/shipments/${shipment["ID Number"]}`}
                            color="primary"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton color="secondary" onClick={() => handleEditClick(shipment)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">
                        No shipments found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Shipment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="ID Number"
            name="idNumber"
            value={editData.idNumber}
            onChange={handleEditChange}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Status"
            name="status"
            value={editData.status}
            onChange={handleEditChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ShipmentsList;
